import { Component, Input, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { CommonService } from "../../../../shared/services/common.service";
import { Subject, takeUntil } from "rxjs";
import {
  DownloadVisitNotesReportRequest,
  FetchSessionDeliveryFilterRequest,
  MarkAttendanceRequest
} from "../../core/sessions-list.actions";
import { Store } from "@ngrx/store";
import * as moment from "moment";
import { markAttendance } from "../../core/sessions-list.selectors";
import {roles} from "../../../../shared/constants/common.constants";

@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.css']
})
export class UserDetailsComponent {
  @Input() user: any;
  @Input() userList: any;
  @Input() sessionId: any;
  @Input() groupId: any;
  @Input() loading: boolean;
  @Input() groupStatus: number;
  @Input() sessionDetails: any;
  @Input() sessionCompleted: boolean;

  @ViewChild('otherAdultsInput') otherAdultsInput: any;
  @ViewChild('otherChildrenInput') otherChildrenInput: any;

  maxNumberOfOthers: number = 99;
  errorMessages: any = {};
  errorMessageMap = {
    sessionStartDate: {
      required: "This field is required."
    },
    startTime: {
      required: "This field is required."
    },
    sessionFormat: {
      required: "Required."
    },
    sessionDelivery: {
      required: "Required."
    },
    duration: {
      required: "Required."
    },
    otherCoaches: {
      required: "This field is required."
    },
    internalNote: {},
    visitNote: {},
    otherAdults: {
      max: 'Maximum limit is 99'
    },
    otherChildren: {
      max: 'Maximum limit is 99'
    }
  };

  form = new FormGroup<any>({
    otherAdults: new FormControl({ value: null, disabled: false }, [Validators.max(this.maxNumberOfOthers)]),
    otherChildren: new FormControl({ value: null, disabled: false }, [Validators.max(this.maxNumberOfOthers)]),
  })

  isPresentValueMap = {
    1: true,
    2: false
  }

  markAttendanceLoading = false

  protected readonly roles = roles;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private commonService: CommonService,
    private store: Store,
  ) {
    this.store.dispatch(FetchSessionDeliveryFilterRequest());

    this.store.select(markAttendance)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.markAttendanceLoading = data.loading;
      })
  }

  ngOnChanges(changes: SimpleChanges) {
    this.prepareForm();
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField))
    };
    
    // If checking a specific field and it has an error, focus on it
    if (currentField && this.errorMessages[currentField]) {
      this.focusOnField(currentField);
    }
  }

  focusOnFirstError() {
    // Check for errors in the "Other" section fields and focus on the first one with an error
    if (this.errorMessages['otherAdults']) {
      this.focusOnField('otherAdults');
    } else if (this.errorMessages['otherChildren']) {
      this.focusOnField('otherChildren');
    }
  }

  focusOnField(fieldName: string) {
    setTimeout(() => {
      try {
        if (fieldName === 'otherAdults' && this.otherAdultsInput?.input?.nativeElement) {
          // Focus on the number input field
          this.otherAdultsInput.input.nativeElement.focus();
        } else if (fieldName === 'otherChildren' && this.otherChildrenInput?.input?.nativeElement) {
          // Focus on the number input field
          this.otherChildrenInput.input.nativeElement.focus();
        }
      } catch (error) {
        console.warn('Could not focus on field:', fieldName, error);
      }
    }, 100); // Small delay to ensure error messages are rendered
  }


  save() {
    if (this.commonService.hasDirtyAndInvalidValues(null, this.form).length) {
      this.checkForErrors();
      this.focusOnFirstError();
      return
    }

    if (!this.form.dirty) {
      return;
    }

    const formData = this.form.getRawValue();
    let participantAttendance: any;

    let participantData = formData.userList[0]

    let selectedCoaches = []
    let selectedOtherCoaches = []
    let removedCoachAttendances = []

    // find coaches whose attendance removed
    const removedAttendanceCoaches = this.userList[0]?.sessionData?.coachAttendance.filter((item) => !participantData.coaches?.includes(item.id) && !participantData.otherCoaches?.includes(item.id));

    if (participantData.coaches?.length > 0) {
      selectedCoaches = participantData.coaches.map(eachCoach => {
        return { coachId: eachCoach, isPresent: true };
      });
    }

    if (participantData.otherCoaches?.length > 0) {
      selectedOtherCoaches = participantData.otherCoaches.map(eachCoach => {
        return { coachId: eachCoach, isPresent: true };
      });
    }

    if (removedAttendanceCoaches?.length) {
      removedCoachAttendances = removedAttendanceCoaches.map(eachCoach => {
        return { coachId: eachCoach.id, isPresent: false };
      });
    }
    const participant = {
      participantId: participantData.id,
      isPresent: this.isPresentValueMap[participantData.type],
      otherAdultsAttending: formData.otherAdults,
      otherChildrenAttending: formData.otherChildren,
      sessionData: {
        meetingOn: [
          moment(participantData.sessionStartDate)?.toISOString().split('T')[0],
          moment(participantData.startTime)?.toISOString().split('T')[1]
        ].join('T'),
        location: participantData.sessionDelivery,
        duration: participantData.duration,
        format: participantData.sessionFormat,
        visitNote: participantData.visitNote,
        internalNote: participantData.internalNote,
        coachAttendance: [
          ...(selectedCoaches || []), ...(selectedOtherCoaches || []),
          ...(removedCoachAttendances || [])
        ],
      },
    }

    let adultList = formData?.userList?.slice(1)

    const adults = adultList?.map((adult, index) => {
      let selectedAdultCoaches = []
      let selectedAdultOtherCoaches = []
      let removedAdultCoachAttendances = []

      // find coaches whose attendance removed
      const removedAdultAttendanceCoaches = this.userList[index + 1]?.sessionData?.coachAttendance.filter((item) => !adult?.coaches?.includes(item.id) && !adult?.otherCoaches?.includes(item.id));

      if (adult?.coaches?.length > 0) {
        selectedAdultCoaches = adult?.coaches?.map(eachCoach => {
          return { coachId: eachCoach, isPresent: true };
        });
      }

      if (adult?.otherCoaches?.length > 0) {
        selectedAdultOtherCoaches = adult?.otherCoaches?.map(eachCoach => {
          return { coachId: eachCoach, isPresent: true };
        });
      }

      if (removedAdultAttendanceCoaches?.length) {
        removedAdultCoachAttendances = removedAdultAttendanceCoaches.map(eachCoach => {
          return { coachId: eachCoach.id, isPresent: false };
        });
      }

      return {
        id: adult.id,
        isPresent: this.isPresentValueMap[adult.type] !== undefined ? this.isPresentValueMap[adult.type] : null,
        sessionData: {
          meetingOn: [
            moment(adult.sessionStartDate)?.toISOString().split('T')[0],
            moment(adult.startTime)?.toISOString().split('T')[1]
          ].join('T'),
          location: adult.sessionDelivery,
          duration: adult.duration,
          format: adult.sessionFormat,
          visitNote: adult.visitNote,
          internalNote: adult.internalNote,
          coachAttendance: [
            ...(selectedAdultCoaches || []),
            ...(selectedAdultOtherCoaches || []),
            ...(removedAdultCoachAttendances || [])
          ],
        },
      };
    });

    if (!participant.sessionData.internalNote) {
      delete participant.sessionData.internalNote;
    }

    if (!participant.sessionData.visitNote) {
      delete participant.sessionData.visitNote;
    }

    for (let adult of adults) {
      if (!adult.sessionData.internalNote) {
        delete adult.sessionData.internalNote;
      }

      if (!adult.sessionData.visitNote) {
        delete adult.sessionData.visitNote;
      }
    }

    participantAttendance = [
      {
        ...participant,
        adults: adults
      }
    ]

    const payload = {
      participantAttendance,
      groupId: this.groupId,
      sessionId: this.sessionId
    }

    this.store.dispatch(MarkAttendanceRequest({ payload }))
  }

  prepareForm() {
    const userListFormArray: FormArray = new FormArray([])

    if (this.userList.length > 0) {
      this.userList?.forEach(() => {
        const attendanceForm: any = new FormGroup<any>({
          id: new FormControl({ value: null, disabled: false }),
          type: new FormControl({ value: null, disabled: false }),
          typeMobile: new FormControl({ value: null, disabled: false }),
          sessionStartDate: new FormControl({ value: "", disabled: false }, {
            validators: [
              Validators.required
            ]
          }),
          startTime: new FormControl({ value: "", disabled: false }, {
            validators: [
              Validators.required
            ]
          }),
          sessionFormat: new FormControl({ value: null, disabled: false }, {
            validators: [
              Validators.required
            ]
          }),
          sessionDelivery: new FormControl({ value: null, disabled: false }, {
            validators: [
              Validators.required
            ]
          }),
          duration: new FormControl({ value: null, disabled: false }, {
            validators: [
              Validators.required
            ]
          }),
          internalNote: new FormControl({ value: null, disabled: false }),
          visitNote: new FormControl({ value: null, disabled: false }),
          coaches: new FormControl([]),
        });
        userListFormArray.push(attendanceForm);
      })
    }

    this.form.removeControl('userList');
    this.form.addControl('userList', userListFormArray);

    // Populate otherAdults and otherChildren from the participant's sessionData
    if (this.userList && this.userList.length > 0 && this.userList[0]?.sessionData) {
      const participantSessionData = this.userList[0].sessionData;
      
      // Set values from API response (including 0 as a valid value)
      const otherAdults = participantSessionData.otherAdultsAttending;
      const otherChildren = participantSessionData.otherChildrenAttending;
      
      this.form.get('otherAdults')?.setValue(otherAdults ?? null);
      this.form.get('otherChildren')?.setValue(otherChildren ?? null);
    }
  }

  handleDownloadVisitNotesClick() {
  }

  downloadVisitNotes(data) {
    this.store.dispatch(DownloadVisitNotesReportRequest({
      payload: {
        forUserId: data,
        groupId: this.groupId,
        sessionId: this.sessionId
      }
    }))
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
